import type { Language } from '@eformulieren/engine'
import { AdviesAnkerSoort, AdviesEindreden, AdviesTermijn, AdviesToestand, BelangPartijen, NwoEmployee, Role, Side } from '@eformulieren/model'
import type { AdviceRequestView } from '@eformulieren/renderer'
import { z } from 'zod'
import type { PersonLabel } from './sharing.js'
import type { Bilingual } from '@eformulieren/renderer'

/**
 * Wat de pagina van `GET /advies` maakt voor het adviespaneel en de adviseurpagina (R-184, R-184b,
 * R-184c, R-184h, ontwerp 7.4a, `api-contract.md` 6 en "Belangenverklaring").
 *
 * **Niet strikt gelezen**, met opzet en zoals `AdviceSummary` in `sharing.ts`: de pod past het verzoek
 * aan de kijker aan (`mijnRol`, aan de externe kant het merkteken van NWO, en voor de adviseur
 * `partijen`). Wat hier gelezen wordt, gaat niet verder dan dit bestand als sleutel.
 *
 * **Elke persoon wordt tekst** (`adviceViews`): de kijker zelf "U"; de adviseur met het adres uit de
 * lijst Wie heeft toegang (de regel van zijn uitnodiging), anders uit `labels`, anders als rol
 * ("Adviseur binnen NWO"), dezelfde regel als in die lijst; het merkteken van NWO "NWO"; een andere
 * vrager "Medewerker van NWO". Het anker in de taal van de lezer uit de projectie, met het label van
 * de opslag (Nederlands) als terugval.
 */

const ShownPerson = z.union([z.string().min(1), NwoEmployee])
export type ShownPerson = z.infer<typeof ShownPerson>

const AdviceRecord = z.object({
  id: z.string().min(1),
  uitnodigingId: z.string().min(1),
  toestand: AdviesToestand,
  anker: z.object({
    soort: AdviesAnkerSoort,
    sectieId: z.string().nullable(),
    veldId: z.string().nullable(),
    label: z.string().min(1),
  }),
  vrager: ShownPerson,
  vragerRol: Role,
  adviseurKant: Side,
  adviseur: z.string().nullable(),
  gedeeld: z.array(z.string()),
  termijn: AdviesTermijn.nullable(),
  reacties: z.array(
    z.object({
      id: z.string().min(1),
      persoon: ShownPerson,
      rol: z.enum(['vrager', 'adviseur']),
      tekst: z.string(),
      moment: z.string(),
    }),
  ),
  eindreden: AdviesEindreden.nullable(),
  /**
   * De uitkomst: welke stap de adviseurpagina toont (`adviserStep.ts`). En bij een belang de toelichting,
   * voor de kaart van de vrager; de pod stuurt die alleen aan de interne kant (`forViewer`).
   */
  belangenverklaring: z
    .object({
      uitkomst: z.enum(['geen-belang', 'belang']),
      toelichting: z.string().nullable().optional(),
      moment: z.string().optional(),
      /** Wat de adviseur zag toen hij verklaarde (R-184j). */
      partijen: BelangPartijen.nullable().optional(),
    })
    .nullable(),
  /** De oordelen van NWO over de verklaring, oudste eerst; het laatste geldt (R-184k). */
  nwoOordelen: z
    .array(
      z.object({
        uitkomst: z.enum(['geen-belang', 'belang']),
        toelichting: z.string(),
        persoon: ShownPerson,
        rol: z.enum(['vrager', 'beheer']),
        moment: z.string(),
      }),
    )
    .optional(),
  /** Of deze kijker het oordeel van NWO kan geven, volgens de pod. */
  kanBeoordelen: z.boolean().optional(),
  mijnRol: z.enum(['vrager', 'adviseur']),
  /** Het anker in beide talen, van de pod (`ankerTekst` in `advies.ts` van de pod). */
  ankerTekst: z.object({ nl: z.string(), en: z.string() }).optional(),
  partijen: BelangPartijen.nullable().optional(),
})
export type AdviceRecord = z.infer<typeof AdviceRecord>

const AdviceFull = z.object({
  verzoeken: z.array(AdviceRecord),
  kanVragen: z.boolean(),
  adviseurBuitenNwo: z.boolean().optional(),
  uitgeslotenSecties: z.array(z.string()).optional(),
})

export interface ReadAdviceFull {
  readonly kanVragen: boolean
  readonly buitenNwo: boolean
  readonly uitgesloten: readonly string[]
  readonly verzoeken: readonly AdviceRecord[]
}

/** `GET /advies` met het gesprek erbij, of niets als het niet op de vorm past. */
export function readAdviceFull(body: unknown): ReadAdviceFull | undefined {
  const read = AdviceFull.safeParse(body)
  if (!read.success) return undefined
  return {
    kanVragen: read.data.kanVragen,
    buitenNwo: read.data.adviseurBuitenNwo ?? false,
    uitgesloten: read.data.uitgeslotenSecties ?? [],
    verzoeken: read.data.verzoeken,
  }
}

/** De verzoeken die als kaart in het paneel staan: die van de vragers, niet een eigen verzoek als adviseur. */
export function panelRequests(read: ReadAdviceFull | undefined): readonly AdviceRecord[] {
  return read?.verzoeken.filter((request) => request.mijnRol === 'vrager') ?? []
}

/**
 * Of het paneel er staat (R-184b): alleen aan de interne kant, en alleen als er advies is voor deze
 * kijker, een verzoek als vrager of de mogelijkheid om te vragen. Anders ook geen lege plek.
 */
export function showAdvicePanel(side: Side, read: ReadAdviceFull | undefined): boolean {
  if (side !== 'intern' || read === undefined) return false
  return read.kanVragen || panelRequests(read).length > 0
}

/** Wat `adviceViews` nodig heeft om personen en ankers in woorden te zetten. */
export interface AdviceNames {
  /** De vaste sleutel van de kijker (`/whoami`). */
  readonly ik: string
  readonly taal: Language
  readonly sectionName: (sectionId: string) => string | undefined
  readonly fieldName: (fieldId: string) => string | undefined
  /** De titel van het formulier, voor het formulier als geheel. */
  readonly formName: string
  /** Het adres op de regel van deze uitnodiging in de lijst Wie heeft toegang, als de kijker het mag zien. */
  readonly emailOfInvitation: (invitationId: string) => string | undefined
  readonly labels: Readonly<Record<string, PersonLabel>>
}

const ADVISER: Readonly<Record<Side, Bilingual>> = {
  intern: { nl: 'Adviseur binnen NWO', en: 'Adviser within NWO' },
  extern: { nl: 'Adviseur buiten NWO', en: 'Adviser outside NWO' },
}
const YOU: Bilingual = { nl: 'U', en: 'You' }
const NWO: Bilingual = { nl: 'NWO', en: 'NWO' }
const EMPLOYEE: Bilingual = { nl: 'Medewerker van NWO', en: 'NWO employee' }
const ADMIN: Bilingual = { nl: 'Functioneel beheer van NWO', en: 'NWO functional management' }

function isMarker(person: ShownPerson): person is z.infer<typeof NwoEmployee> {
  return typeof person !== 'string'
}

/** De adviseur zoals de kijker hem mag zien; dezelfde regel als in de lijst Wie heeft toegang. */
function adviserName(request: AdviceRecord, names: AdviceNames): string {
  if (request.adviseur !== null && request.adviseur === names.ik) return YOU[names.taal]
  const fromList = names.emailOfInvitation(request.uitnodigingId)
  if (fromList !== undefined) return fromList
  const fromLabel = request.adviseur === null ? undefined : names.labels[request.adviseur]?.email
  return fromLabel ?? ADVISER[request.adviseurKant][names.taal]
}

function anchorLabel(request: AdviceRecord, names: AdviceNames): string {
  const anchor = request.anker
  if (anchor.soort === 'formulier') return names.formName
  // Zonder projectie (een adviseur vóór zijn verklaring) de tekst van de pod in de taal van de lezer, en
  // pas daarna het Nederlandse label van het verzoek.
  const fallback = request.ankerTekst?.[names.taal] ?? anchor.label
  if (anchor.veldId !== null) return names.fieldName(anchor.veldId) ?? fallback
  return (anchor.sectieId === null ? undefined : names.sectionName(anchor.sectieId)) ?? fallback
}

/** De verzoeken zoals het paneel en de adviseurpagina ze tonen: alleen tekst, geen sleutel. */
export function adviceViews(requests: readonly AdviceRecord[], names: AdviceNames): readonly AdviceRequestView[] {
  return requests.map((request) => {
    const adviser = adviserName(request, names)
    const asker = request.mijnRol === 'adviseur' ? askerName(request.vrager, request.vragerRol, names.taal) : undefined
    return {
      id: request.id,
      toestand: request.toestand,
      anker: { soort: request.anker.soort, sectieId: request.anker.sectieId, veldId: request.anker.veldId, label: anchorLabel(request, names) },
      adviseur: adviser,
      termijn: request.termijn,
      ...(request.belangenverklaring?.uitkomst === 'belang' && typeof request.belangenverklaring.toelichting === 'string' && request.belangenverklaring.toelichting !== ''
        ? { belangToelichting: request.belangenverklaring.toelichting }
        : {}),
      // De verklaring op de kaart van de vrager (R-184d, R-184j); de adviseur kent haar zelf.
      ...(request.mijnRol === 'vrager' && request.belangenverklaring !== null && request.belangenverklaring.moment !== undefined
        ? {
            verklaring: {
              uitkomst: request.belangenverklaring.uitkomst,
              toelichting: request.belangenverklaring.toelichting ?? null,
              moment: request.belangenverklaring.moment,
              partijen: request.belangenverklaring.partijen ?? null,
            },
          }
        : {}),
      oordelen: (request.nwoOordelen ?? []).map((judgement) => ({
        uitkomst: judgement.uitkomst,
        toelichting: judgement.toelichting,
        wie: judgementBy(judgement.persoon, judgement.rol, names),
        moment: judgement.moment,
      })),
      kanBeoordelen: request.kanBeoordelen === true,
      reacties: request.reacties.map((reaction) => {
        const person = reaction.persoon
        const who =
          !isMarker(person) && person === names.ik
            ? YOU[names.taal]
            : isMarker(person)
              ? NWO[names.taal]
              : reaction.rol === 'adviseur'
                ? adviser
                : asker !== undefined
                  ? capital(asker)
                  : EMPLOYEE[names.taal]
        return { id: reaction.id, wie: who, rol: reaction.rol, tekst: reaction.tekst, moment: reaction.moment }
      }),
    }
  })
}

/** Wie oordeelde, als tekst: de kijker "U", aan de externe kant "NWO", anders de weg waarlangs. */
function judgementBy(person: ShownPerson, role: 'vrager' | 'beheer', names: AdviceNames): string {
  if (isMarker(person)) return NWO[names.taal]
  if (person === names.ik) return YOU[names.taal]
  return (role === 'beheer' ? ADMIN : EMPLOYEE)[names.taal]
}

const ASKER: Readonly<Record<'toetser' | 'anders', Bilingual>> = {
  toetser: { nl: 'de toetser', en: 'the reviewer' },
  anders: { nl: 'een medewerker van NWO', en: 'an NWO employee' },
}

/**
 * Wie de vraag stelde, voor de adviseur: buiten NWO "NWO" (de pod geeft dan het merkteken, R-66a),
 * binnen NWO de rol waarin gevraagd is. Nooit een naam of sleutel: die heeft de pagina niet.
 */
export function askerName(asker: ShownPerson, role: z.infer<typeof Role>, language: Language): string {
  if (isMarker(asker)) return NWO[language]
  return ASKER[role === 'toetser' ? 'toetser' : 'anders'][language]
}

function capital(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}
